<template>
  <div class="entry-view">
    <div v-if="loading" class="loading">
      <div class="spinner"></div>
      <p>Loading...</p>
    </div>
    
    <div v-else-if="error" class="error">
      <h2>Error</h2>
      <p>{{ error }}</p>
      <button @click="$router.push('/')" class="home-btn">Go to Home</button>
    </div>
    
    <div v-else-if="entry" class="entry-container">
      <!-- Header with metadata -->
      <div class="entry-header">
        <div class="entry-info">
          <h3 v-if="entry.name" class="entry-title">{{ entry.name }}</h3>
          <div class="entry-meta-row">
            <span 
              v-if="entry.content_type" 
              class="entry-type" 
              :style="{ backgroundColor: getLanguageColor(entry.content_type) }"
            >
              {{ getLanguageLabel(entry.content_type) }}
            </span>
            <span class="entry-date">{{ formatDate(entry.created_at) }}</span>
          </div>
        </div>
        <button @click="$router.push('/')" class="home-btn-top" title="Go to Home">
          <Icon name="home" :size="20" />
        </button>
      </div>
      
      <!-- Content in iframe for pure isolation -->
      <div class="iframe-container">
        <iframe 
          ref="contentFrame"
          class="content-iframe"
          frameborder="0"
          scrolling="auto"
          sandbox="allow-scripts allow-same-origin allow-popups allow-modals allow-downloads"
          @load="onIframeLoad"
        ></iframe>
      </div>
      
      <!-- Footer with actions -->
      <div class="entry-footer">
        <div class="entry-meta">
          <span class="meta-item">
            <Icon name="clock" :size="16" />
            Created {{ formatDate(entry.created_at) }}
          </span>
          <span v-if="entry.updated_at && entry.updated_at !== entry.created_at" class="meta-item">
            <Icon name="edit" :size="16" />
            Updated {{ formatDate(entry.updated_at) }}
          </span>
        </div>
        <div class="action-buttons">
          <button @click="copyContent" class="copy-btn">
            <Icon name="copy" :size="16" />
            Copy Content
          </button>
          <button @click="copyUrl" class="copy-url-btn">
            <Icon name="link" :size="16" />
            Copy URL
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { entryService } from '@/services/api';
import Icon from '@/components/Icon.vue';
import { markdownService } from '@/services/markdownService';
import '@/styles/markdown.css';
// import { buildFileUrl } from '@/config';

export default {
  name: 'EntryView',
  components: {
    Icon
  },
  data() {
    return {
      entry: null,
      loading: true,
      error: null
    };
  },
  computed: {
    uniqueId() {
      return this.$route.params.uniqueId;
    }
  },
  async created() {
    await this.fetchEntry();
  },
  mounted() {
    if (this.entry) {
      this.loadContentIntoIframe();
    }
  },
  updated() {
    if (this.entry && this.$refs.contentFrame) {
      this.loadContentIntoIframe();
    }
  },
  methods: {


    async fetchEntry() {
      try {
        this.loading = true;
        const response = await entryService.getEntryInfo(this.uniqueId);
        this.entry = response.data;
        this.error = null;
        
        // Load content into iframe after entry is fetched
        this.$nextTick(() => {
          this.loadContentIntoIframe();
        });
      } catch (error) {
        console.error('Error fetching entry:', error);
        if (error.response && error.response.status === 404) {
          this.error = 'Entry not found';
        } else {
          this.error = 'Failed to load entry. Please try again.';
        }
      } finally {
        this.loading = false;
      }
    },

    renderMarkdown(content) {
      return markdownService.render(content);
    },

    isMarkdownContent(contentType) {
      return markdownService.isMarkdownContent(contentType);
    },



    loadContentIntoIframe() {
      if (!this.entry || !this.$refs.contentFrame) return;
      
      const iframe = this.$refs.contentFrame;
      let htmlContent;
      
      if (this.isMarkdownContent(this.entry.content_type)) {
        // Convert markdown to HTML and wrap in basic document
        const renderedMarkdown = this.renderMarkdown(this.entry.content);
        htmlContent = this.createHtmlDocument(renderedMarkdown, 'Markdown Content');
      } else {
        // For other content types, check if it contains HTML
        if (this.isCompleteHtmlDocument(this.entry.content)) {
          // Check if it's a complete HTML document or HTML fragment
          const trimmed = this.entry.content.trim().toLowerCase();
          if (trimmed.startsWith('<!doctype') || trimmed.startsWith('<html')) {
            // Complete HTML document - use as is
            htmlContent = this.entry.content;
          } else {
            // HTML fragment - wrap in basic document structure
            htmlContent = this.createHtmlDocument(this.entry.content, 'HTML Content');
          }
        } else {
          // Plain text - wrap in <pre> tag
          htmlContent = this.createHtmlDocument(
            `<pre style="margin: 0; font-family: monospace; white-space: pre-wrap; word-wrap: break-word;">${this.escapeHtml(this.entry.content)}</pre>`,
            'Text Content'
          );
        }
      }
      
      // Write content to iframe
      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(htmlContent);
      doc.close();
    },

    createHtmlDocument(bodyContent, title = 'Content') {
      return `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${title}</title>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif;
            line-height: 1.6;
            color: #333;
            margin: 0;
            padding: 20px;
            background: white;
        }
        h1, h2, h3, h4, h5, h6 {
            margin-top: 24px;
            margin-bottom: 16px;
            font-weight: 600;
            line-height: 1.25;
            color: #24292e;
        }
        h1 { font-size: 2em; border-bottom: 1px solid #eaecef; padding-bottom: 0.3em; }
        h2 { font-size: 1.5em; border-bottom: 1px solid #eaecef; padding-bottom: 0.3em; }
        p { margin-bottom: 16px; }
        code {
            background-color: rgba(27, 31, 35, 0.05);
            border-radius: 3px;
            font-size: 85%;
            margin: 0;
            padding: 0.2em 0.4em;
            font-family: 'SFMono-Regular', 'Consolas', 'Liberation Mono', 'Menlo', monospace;
        }
        pre {
            background-color: #f6f8fa;
            border-radius: 6px;
            font-size: 85%;
            line-height: 1.45;
            overflow: auto;
            padding: 16px;
            margin-bottom: 16px;
            font-family: 'SFMono-Regular', 'Consolas', 'Liberation Mono', 'Menlo', monospace;
        }
        pre code { background-color: transparent; border-radius: 0; padding: 0; }
        blockquote {
            border-left: 4px solid #dfe2e5;
            color: #6a737d;
            padding: 0 16px;
            margin: 0 0 16px 0;
        }
        ul, ol { margin-bottom: 16px; padding-left: 2em; }
        li { margin-bottom: 4px; }
        
        /* Task list styles */
        .task-list-item {
          list-style-type: none;
          margin: 0.25em 0;
          padding-left: 0;
        }
        .task-list-item-checkbox {
          margin-right: 0.5em;
          margin-left: -1.4em;
          vertical-align: middle;
          transform: scale(1.2);
          accent-color: #42b983;
        }
        .task-list-item-checkbox:checked {
          accent-color: #42b983;
        }
        .task-list-item.checked {
          opacity: 0.7;
          text-decoration: line-through;
          color: #6a737d;
        }
        .task-list-item.checked .task-list-item-checkbox {
          opacity: 1;
        }
        table {
            border-collapse: collapse;
            border-spacing: 0;
            width: 100%;
            margin-bottom: 16px;
        }
        table th, table td {
            border: 1px solid #dfe2e5;
            padding: 6px 13px;
        }
        table th {
            background-color: #f6f8fa;
            font-weight: 600;
        }
        img { max-width: 100%; height: auto; }
        a { color: #0366d6; text-decoration: none; }
        a:hover { text-decoration: underline; }
        
        /* File reference styles */
        .markdown-file-image {
          border: 2px solid #42b983;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(66, 185, 131, 0.2);
          transition: all 0.3s ease;
        }
        .markdown-file-image:hover {
          border-color: #36996b;
          box-shadow: 0 4px 16px rgba(66, 185, 131, 0.3);
          transform: scale(1.02);
        }
        .markdown-file-image-error {
          border: 2px solid #dc3545;
          border-radius: 8px;
          background-color: #f8d7da;
          padding: 20px;
          max-width: 300px;
        }
        .markdown-file-link {
          color: #42b983;
          border-bottom: 1px solid #42b983;
          padding: 2px 4px;
          border-radius: 3px;
          background: rgba(66, 185, 131, 0.1);
          transition: all 0.2s ease;
        }
        .markdown-file-link:hover {
          background: rgba(66, 185, 131, 0.2);
          border-bottom-color: #36996b;
          text-decoration: none;
        }
        .markdown-file-link-error {
          color: #dc3545;
          text-decoration: line-through;
          border-bottom: 1px dashed #dc3545;
          padding: 2px 4px;
          border-radius: 3px;
          background: rgba(220, 53, 69, 0.1);
          cursor: not-allowed;
        }
    </style>
</head>
<body>
${bodyContent}
</body>
</html>`;
    },

    isCompleteHtmlDocument(content) {
      if (!content) return false;
      const trimmed = content.trim().toLowerCase();
      
      // Check for complete HTML document
      if (trimmed.startsWith('<!doctype') || trimmed.startsWith('<html')) {
        return true;
      }
      
      // Check for HTML fragments with common HTML tags
      const htmlTags = ['<html', '<head', '<body', '<div', '<h1', '<h2', '<h3', '<h4', '<h5', '<h6', 
                       '<p>', '<span', '<script', '<style', '<button', '<input', '<form'];
      
      return htmlTags.some(tag => trimmed.includes(tag));
    },

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },

    onIframeLoad() {
      // Handle iframe load events if needed
      console.log('Iframe content loaded');
    },

    getLanguageColor(contentType) {
      const colors = {
        'javascript': '#f7df1e',
        'python': '#3776ab',
        'html': '#e34c26',
        'css': '#1572b6',
        'json': '#000000',
        'sql': '#336791',
        'shell': '#4eaa25',
        'yaml': '#cb171e',
        'markdown': '#083fa1',
        'xml': '#0060ac',
        'text': '#6c757d'
      };
      return colors[contentType] || '#6c757d';
    },

    getLanguageLabel(contentType) {
      const labels = {
        'javascript': 'JavaScript',
        'python': 'Python',
        'html': 'HTML',
        'css': 'CSS',
        'json': 'JSON',
        'sql': 'SQL',
        'shell': 'Shell',
        'yaml': 'YAML',
        'markdown': 'Markdown',
        'xml': 'XML',
        'text': 'Text'
      };
      return labels[contentType] || 'Text';
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString();
    },

    async copyContent() {
      if (!this.entry?.content) return;
      
      try {
        await navigator.clipboard.writeText(this.entry.content);
        this.showToast('Content copied to clipboard');
      } catch (error) {
        console.error('Failed to copy content:', error);
        this.showToast('Failed to copy content');
      }
    },

    async copyUrl() {
      try {
        await navigator.clipboard.writeText(window.location.href);
        this.showToast('URL copied to clipboard');
      } catch (error) {
        console.error('Failed to copy URL:', error);
        this.showToast('Failed to copy URL');
      }
    },

    showToast(message) {
      const toast = document.createElement('div');
      toast.className = 'toast-notification';
      toast.textContent = message;
      document.body.appendChild(toast);
      
      setTimeout(() => {
        toast.classList.add('toast-fade-out');
        setTimeout(() => {
          if (document.body.contains(toast)) {
            document.body.removeChild(toast);
          }
        }, 500);
      }, 2000);
    }
  }
};
</script>

<style scoped>
.entry-view {
  min-height: 100vh;
  background-color: #f8f9fa;
  display: flex;
  flex-direction: column;
  padding: 0; /* Remove padding for full screen */
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 50vh;
  color: #666;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e9ecef;
  border-top: 4px solid #42b983;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error {
  max-width: 600px;
  margin: 50px auto;
  text-align: center;
  padding: 40px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.error h2 {
  color: #dc3545;
  margin-bottom: 16px;
}

.home-btn {
  background-color: #6c757d;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 16px;
}

.home-btn:hover {
  background-color: #5a6268;
}

.entry-container {
  width: 100%;
  height: 100vh;
  background: white;
  display: flex;
  flex-direction: column;
  overflow: hidden; /* Prevent container overflow */
}

.entry-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  flex-shrink: 0;
  gap: 16px;
  position: sticky;
  top: 0;
  z-index: 100;
}

.entry-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.entry-title {
  margin: 0;
  /* font-size: 24px;
  font-weight: 600; */
  color: #2c3e50;
  line-height: 1.2;
  word-break: break-word;
}

.entry-meta-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.entry-type {
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
}

.entry-date {
  color: #6c757d;
  font-size: 14px;
}

.home-btn-top {
  padding: 8px;
  border: none;
  border-radius: 8px;
  background-color: #42b983;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 40px;
  height: 40px;
  box-shadow: 0 2px 6px rgba(66, 185, 131, 0.2);
  flex-shrink: 0;
  margin-top: 2px; /* Align with title */
}

.home-btn-top:hover {
  background-color: #36996b;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(66, 185, 131, 0.3);
}

.iframe-container {
  width: 100%;
  position: relative;
  background: white;
  flex: 1; /* Take up all remaining space */
  overflow: hidden;
}

.content-iframe {
  width: 100%;
  height: 100%; /* Fill the entire iframe container */
  border: none;
  background: white;
}

.entry-footer {
  padding: 20px 24px;
  border-top: 1px solid #e9ecef;
  background-color: #f8f9fa;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  flex-shrink: 0;
  position: sticky;
  bottom: 0;
  z-index: 100;
}

.entry-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  color: #6c757d;
  font-size: 14px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.copy-btn,
.copy-url-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.copy-btn {
  background-color: #42b983;
  color: white;
}

.copy-btn:hover {
  background-color: #36996b;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(66, 185, 131, 0.3);
}

.copy-url-btn {
  background-color: #6c757d;
  color: white;
}

.copy-url-btn:hover {
  background-color: #5a6268;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(108, 117, 125, 0.3);
}

@media (max-width: 768px) {
  .entry-view {
    padding: 10px;
  }
  
  .entry-header {
    padding: 16px;
    flex-direction: column;
    gap: 12px;
    text-align: center;
  }
  
  .entry-footer {
    padding: 16px;
    flex-direction: column;
    align-items: stretch;
  }
  
  .action-buttons {
    width: 100%;
    justify-content: center;
  }
}

/* Toast notification styles */
.toast-notification {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 10px 20px;
  border-radius: 4px;
  font-size: 14px;
  z-index: 1000;
  animation: toast-in 0.3s ease;
}

.toast-fade-out {
  opacity: 0;
  transition: opacity 0.5s;
}

@keyframes toast-in {
  from { transform: translate(-50%, 20px); opacity: 0; }
  to { transform: translate(-50%, 0); opacity: 1; }
}
</style>
